import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { EmployeesService } from '../shared/services/employees.service';
import { MyTeamMember, MyTeamResponse } from '../shared/models/employee';
import { AssignedCourseItem, AssignedCoursesResponse, Course, CourseResponse } from '../shared/models/course';
import { CoursesService } from '../shared/services/courses.service';
import { catchError, map } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { BulkAssignDialogComponent } from '../shared/components/bulk-assign-dialog/bulk-assign-dialog.component';

@Component({
  selector: 'app-team',
  standalone: true,
  imports: [CommonModule, BulkAssignDialogComponent],
  templateUrl: './team.component.html',
  styleUrls: ['./team.component.scss']
})
export class TeamComponent implements OnInit {
  readonly loading = signal<boolean>(false);
  readonly errorText = signal<string>('');
  private errorTimer: any = null;

  readonly managerEmail = signal<string>('');
  readonly teamCount = signal<number>(0);
  readonly teamMembers = signal<MyTeamMember[]>([]);

  readonly myTeamExpanded = signal<boolean>(true);
  readonly expandedMembers = signal<Set<string>>(new Set());

  // Assigned courses cache per member
  readonly assignedMap = signal<Map<string, { loading: boolean; courses?: AssignedCourseItem[]; error?: string }>>(new Map());

  // Course names cache (courseId -> courseName)
  readonly courseNameMap = signal<Map<string, string>>(new Map());

  // Assign Course dialog state
  readonly assignOpen = signal<boolean>(false);
  readonly assignMember = signal<string | null>(null);
  readonly assignLoading = signal<boolean>(false);
  readonly assignError = signal<string>('');
  private assignErrorTimer: any = null;
  readonly availableCourses = signal<Course[]>([]);
  readonly selectedCourseId = signal<string>('');

  // Bulk assign state
  readonly bulkAssignOpen = signal<boolean>(false);
  readonly bulkAssignMembers = signal<Array<{id: string, name: string}>>([]);
  readonly bulkAssignCourses = signal<Course[]>([]);
  readonly bulkAssignCoursesLoading = signal<boolean>(false);

  constructor(
    private readonly employees: EmployeesService,
    private readonly courses: CoursesService
  ) {}

  ngOnInit(): void {
    this.fetchMyTeam();
  }

  private normalizeMember(member: string | null | undefined): string | null {
    const v = (member ?? '').trim();
    return v ? v.toUpperCase() : null;
  }

  displayName(m: MyTeamMember): string {
    const name = (m?.NAME ?? '').trim();
    if (name) return name;
    const email = (m?.EMAIL ?? '').trim();
    if (email) return email;
    const memberId = (m?.MEMBER_ID ?? '').trim();
    return memberId || '(unknown)';
  }

  private safeName(m: MyTeamMember): string {
    return (m?.NAME ?? '').trim();
  }
  private safeEmail(m: MyTeamMember): string {
    const email = (m?.EMAIL ?? '').trim();
    if (email) return email;
    return (m?.MEMBER_ID ?? '').trim();
  }
  formatHeaderTitle(m: MyTeamMember): string {
    const name = this.safeName(m);
    const email = this.safeEmail(m);
    if (name && email && name.toUpperCase() !== email.toUpperCase()) {
      return `${name} (${email})`;
    }
    return name || email || '(unknown)';
  }
  formatLevel(m: MyTeamMember): string {
    return (m?.LEVEL ?? '').trim();
  }

  memberKey(m: MyTeamMember): string | null {
    return this.normalizeMember(m?.EMAIL) ?? this.normalizeMember(m?.MEMBER_ID);
  }

  fetchMyTeam(): void {
    this.loading.set(true);
    this.errorText.set('');
    this.employees.getMyTeam().subscribe({
      next: (resp: MyTeamResponse) => {
        this.loading.set(false);
        const body = resp?.body;
        this.managerEmail.set(body?.MANAGER ?? '');
        this.teamCount.set(body?.COUNT ?? 0);
        this.teamMembers.set(Array.isArray(body?.TEAM_MEMBERS) ? body!.TEAM_MEMBERS : []);
      },
      error: (err) => {
        this.loading.set(false);
        this.showError(typeof err === 'string' ? err : 'Failed to load team.');
      }
    });
  }

  toggleMyTeam(): void {
    this.myTeamExpanded.set(!this.myTeamExpanded());
  }

  isMemberOpen(memberKey: string | null | undefined): boolean {
    const key = this.normalizeMember(memberKey);
    if (!key) return false;
    return this.expandedMembers().has(key);
  }

  toggleMember(memberKey: string | null | undefined): void {
    const key = this.normalizeMember(memberKey);
    if (!key) return;
    const set = new Set(this.expandedMembers());
    if (set.has(key)) {
      set.delete(key);
    } else {
      set.add(key);
      // fetch assigned if not cached
      if (!this.assignedMap().has(key)) {
        const next = new Map(this.assignedMap());
        next.set(key, { loading: true });
        this.assignedMap.set(next);
        this.employees.getAssignedFor(key).subscribe({
          next: (resp: AssignedCoursesResponse) => {
            const body: any[] = Array.isArray((resp as any)?.body) ? (resp as any).body : [];
            let courses: AssignedCourseItem[] = [];
            if (body.length > 0 && Array.isArray((body[0] as any)?.COURSES)) {
              // Old shape
              courses = body.flatMap((it: any) => Array.isArray(it?.COURSES) ? it.COURSES : []) as AssignedCourseItem[];
            } else {
              // New shape (flat rows)
              courses = body
                .filter((row: any) => row && row.COURSE_ID && row.STATUS)
                .map((row: any) => ({
                  COURSE_ID: String(row.COURSE_ID),
                  STATUS: String(row.STATUS).toUpperCase() as AssignedCourseItem['STATUS'],
                  ASSIGNED_ON: row.ASSIGNED_ON ?? undefined,
                  STARTED_ON: row.STARTED_ON ?? undefined,
                  COMPLETED_ON: row.COMPLETED_ON ?? undefined
                }));
            }
            const updated = new Map(this.assignedMap());
            updated.set(key, { loading: false, courses });
            this.assignedMap.set(updated);
            this.prefetchCourseNames(courses);
          },
          error: (err) => {
            const updated = new Map(this.assignedMap());
            updated.set(key, { loading: false, error: 'Failed to load assigned courses.' });
            this.assignedMap.set(updated);
          }
        });
      }
    }
    this.expandedMembers.set(set);
  }

  assignedFor(memberKey: string | null | undefined) {
    const key = this.normalizeMember(memberKey);
    if (!key) return undefined;
    return this.assignedMap().get(key);
  }

  getCourseName(courseId: string | null | undefined): string {
    if (!courseId) return '';
    return this.courseNameMap().get(courseId) ?? courseId;
  }

  badgeClass(status: string): string {
    const s = (status ?? '').toUpperCase();
    if (s === 'COMPLETED') return 'badge badge--green';
    if (s === 'IN_PROGRESS') return 'badge badge--orange';
    if (s === 'ASSIGNED') return 'badge badge--blue';
    return 'badge';
  }

  dismissError(): void {
    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
      this.errorTimer = null;
    }
    this.errorText.set('');
  }

  private showError(msg: string): void {
    this.errorText.set(msg);
    if (this.errorTimer) clearTimeout(this.errorTimer);
    this.errorTimer = setTimeout(() => {
      this.errorText.set('');
      this.errorTimer = null;
    }, 10000);
  }

  private prefetchCourseNames(items: AssignedCourseItem[]): void {
    const needIds = Array.from(
      new Set(
        (items || [])
          .map(i => i.COURSE_ID)
          .filter(id => !!id && !this.courseNameMap().has(id as string)) as string[]
      )
    );
    if (needIds.length === 0) return;

    const requests = needIds.map(id =>
      this.courses.getCourse(id).pipe(
        map((res: CourseResponse) => ({ id, name: res?.body?.COURSE_NAME ?? id })),
        catchError(() => of({ id, name: id }))
      )
    );

    forkJoin(requests).subscribe((pairs) => {
      const m = new Map(this.courseNameMap());
      for (const p of pairs) {
        m.set(p.id, p.name);
      }
      this.courseNameMap.set(m);
    });
  }

  // Assign Course dialog handlers
  openAssignDialog(memberKey: string | null | undefined): void {
    const key = this.normalizeMember(memberKey);
    if (!key) return;
    this.assignOpen.set(true);
    this.assignMember.set(key);
    this.assignLoading.set(true);
    this.assignError.set('');

    const assigned$ = this.employees.getAssignedFor(key).pipe(
      map((resp: AssignedCoursesResponse) => {
        const body: any[] = Array.isArray((resp as any)?.body) ? (resp as any).body : [];
        if (body.length > 0 && Array.isArray((body[0] as any)?.COURSES)) {
          return body.flatMap((it: any) => Array.isArray(it?.COURSES) ? it.COURSES : []) as AssignedCourseItem[];
        }
        return body
          .filter((row: any) => row && row.COURSE_ID && row.STATUS)
          .map((row: any) => ({
            COURSE_ID: String(row.COURSE_ID),
            STATUS: String(row.STATUS).toUpperCase() as AssignedCourseItem['STATUS'],
            ASSIGNED_ON: row.ASSIGNED_ON ?? undefined,
            STARTED_ON: row.STARTED_ON ?? undefined,
            COMPLETED_ON: row.COMPLETED_ON ?? undefined
          } as AssignedCourseItem));
      }),
      catchError(() => of([] as AssignedCourseItem[]))
    );

    forkJoin([assigned$, this.courses.listCourses()]).subscribe({
      next: ([assigned, listRes]) => {
        const assignedIds = new Set((assigned || []).map(a => a.COURSE_ID));
        const allCourses: Course[] = Array.isArray(listRes?.body) ? listRes.body : [];
        const notAssigned = allCourses.filter(c => !assignedIds.has(c.COURSE_ID));
        this.availableCourses.set(notAssigned);
        this.selectedCourseId.set('');
        this.assignLoading.set(false);
      },
      error: (err) => {
        this.setAssignError(this.describeError(err));
        this.assignLoading.set(false);
      }
    });
  }

  closeAssignDialog(): void {
    this.assignOpen.set(false);
    this.assignMember.set(null);
    this.assignLoading.set(false);
    this.assignError.set('');
    this.availableCourses.set([]);
    this.selectedCourseId.set('');
  }

  onSelectAssignCourse(courseId: string): void {
    this.selectedCourseId.set(courseId);
  }

  confirmAssign(): void {
    const member = this.assignMember();
    const courseId = this.selectedCourseId();
    if (!member || !courseId) return;
    this.assignLoading.set(true);
    this.courses.assignCourse(member, courseId).subscribe({
      next: () => {
        this.assignLoading.set(false);
        this.closeAssignDialog();
        // refresh assigned cache for this member
        const next = new Map(this.assignedMap());
        next.set(member, { loading: true });
        this.assignedMap.set(next);
        this.employees.getAssignedFor(member).subscribe({
          next: (resp: AssignedCoursesResponse) => {
            const body: any[] = Array.isArray((resp as any)?.body) ? (resp as any).body : [];
            let courses: AssignedCourseItem[] = [];
            if (body.length > 0 && Array.isArray((body[0] as any)?.COURSES)) {
              courses = body.flatMap((it: any) => Array.isArray(it?.COURSES) ? it.COURSES : []) as AssignedCourseItem[];
            } else {
              courses = body
                .filter((row: any) => row && row.COURSE_ID && row.STATUS)
                .map((row: any) => ({
                  COURSE_ID: String(row.COURSE_ID),
                  STATUS: String(row.STATUS).toUpperCase() as AssignedCourseItem['STATUS'],
                  ASSIGNED_ON: row.ASSIGNED_ON ?? undefined,
                  STARTED_ON: row.STARTED_ON ?? undefined,
                  COMPLETED_ON: row.COMPLETED_ON ?? undefined
                }));
            }
            const upd = new Map(this.assignedMap());
            upd.set(member, { loading: false, courses });
            this.assignedMap.set(upd);
            this.prefetchCourseNames(courses);
          },
          error: () => {
            const upd = new Map(this.assignedMap());
            upd.set(member, { loading: false, error: 'Failed to refresh assigned courses.' });
            this.assignedMap.set(upd);
          }
        });
      },
      error: (err) => {
        this.setAssignError(this.describeError(err));
        this.assignLoading.set(false);
      }
    });
  }

  dismissAssignError(): void {
    if (this.assignErrorTimer) {
      clearTimeout(this.assignErrorTimer);
      this.assignErrorTimer = null;
    }
    this.assignError.set('');
  }

  private setAssignError(msg: string): void {
    this.assignError.set(msg);
    if (this.assignErrorTimer) clearTimeout(this.assignErrorTimer);
    this.assignErrorTimer = setTimeout(() => {
      this.assignError.set('');
      this.assignErrorTimer = null;
    }, 10000);
  }

  private describeError(err: any): string {
    if (typeof err === 'string') return err;
    if (err?.error?.message) return err.error.message;
    if (err?.message) return err.message;
    return 'Something went wrong. Please contact the administrator.';
  }

  // Bulk assign methods
  openBulkAssign(): void {
    // Prepare members list from visible team members
    const members = this.teamMembers().map(m => ({
      id: this.memberKey(m) || '',
      name: this.displayName(m)
    })).filter(m => m.id);
    
    this.bulkAssignMembers.set(members);
    this.bulkAssignOpen.set(true);
    
    // Set loading state before API call
    this.bulkAssignCoursesLoading.set(true);
    
    // Load all courses
    this.courses.listCourses().subscribe({
      next: (res) => {
        this.bulkAssignCourses.set(Array.isArray(res?.body) ? res.body : []);
        this.bulkAssignCoursesLoading.set(false);
      },
      error: (err) => {
        this.showError(this.describeError(err));
        this.bulkAssignCoursesLoading.set(false);
      }
    });
  }

  closeBulkAssign(): void {
    this.bulkAssignOpen.set(false);
    this.bulkAssignMembers.set([]);
    this.bulkAssignCourses.set([]);
    this.bulkAssignCoursesLoading.set(false);
  }

  onBulkAssignCompleted(): void {
    // Refresh assigned courses cache for all affected members
    // DO NOT call closeBulkAssign() here - let user manually close after reviewing results
    // Just clear the assigned cache to force refresh on next expand
    this.assignedMap.set(new Map());
  }
}


